/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Migrated from: lego/page/etmf/list/apply-package/step1/package-tags/PackageTags.jsx
 *
 * Package tags management component for ETMF package configuration.
 * Handles tag creation and association with packages.
 */

import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { clone, extend, pluck } from 'underscore'
import { Select } from '~/components/Select/Select'
import { useServiceContext } from '~/root'
import Utils from '~/lib/Utils'

interface TagsConfig {
  actions: {
    new: boolean
    associate: boolean
  }
  tagName: string
  tags: Array<{ value: any; label: string }>
}

interface PackageTagsProps {
  packageData: {
    id?: number
    name?: string
    [key: string]: any
  }
  config: any
  options: {
    tagsForPicker: Array<{ id: number | string; name: string }>
  }
  updateConfigData: (config: any) => void
}

const PackageTags: React.FC<PackageTagsProps> = ({ packageData, config, options, updateConfigData }) => {
  const context = useServiceContext()
  const { service, $rootScope } = context
  const { translate } = service.filter
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [conf, setConf] = useState<TagsConfig | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    const unsubscribe = $rootScope.$on('validatePackageForms', (evt: any, data: any) => {
      const configData = data?.configData || data
      validate(configData?.tags)
    })
    return () => unsubscribe()
  }, [$rootScope])

  useEffect(() => {
    if (config && packageData) {
      init(config)
    }
  }, [config, packageData])

  useEffect(() => {
    updateConfigData(conf)
  }, [conf])

  const init = (configDataLocal: any) => {
    if (isInitialLoad) {
      const configDataLocal = clone(config)
      let confDataLocal = clone(conf)
      setIsInitialLoad(false)
      if (!configDataLocal?.tags) {
        extend(configDataLocal, {
          tags: {
            actions: {
              new: false,
              associate: false
            },
            tagName: translate('SETTINGS_PACKAGE_TAGS_CREATE_NEW_PLACEHOLDER_V2', {
              packageName: packageData?.name,
              dateTime: moment.utc(new Date()).format('YYYY-MM-DD hh:mm:ss')
            }),
            tags: []
          }
        })
      }
      confDataLocal = configDataLocal.tags
      setConf(confDataLocal)
    }
  }

  const validate = (confDataRef: TagsConfig) => {
    setIsSubmitted(true)
    if (confDataRef?.actions?.associate) {
      setIsValid(!!confDataRef?.tags?.length)
    }
    $rootScope.$emit('tagsValidation', confDataRef?.actions?.associate ? !!confDataRef?.tags?.length : true)
  }

  return (
    <div className="tags-container">
      <div className="tags-wrapper">
        <div className="form-group mcc-form-group w100p mb0">
          <label>{translate('SETTINGS_PACKAGE_TAGS')}</label>
        </div>
        <div className="tags-options-container">
          <input
            type="checkbox"
            className="mb5 classic package-tags-checkbox"
            id="4f9e1809-9814-48b2-a863-3a5f195452ed"
            name="applyPackageTags"
            checked={conf?.actions?.new}
            onChange={(evt) => {
              const confDataLocal = clone(conf)
              if (confDataLocal) {
                confDataLocal.actions.new = evt.target.checked
                setConf(confDataLocal)
              }
            }}
          />
          <label className="package-tags-label">{translate('SETTINGS_PACKAGE_TAGS_CREATE_NEW')}</label>
        </div>
        {conf?.actions?.new && (
          <div className="ml25">
            <div className="form-group md-form-group w100p mb5">
              <input
                type="text"
                className="md-input fs12-imp"
                id="0e9fb2de-8fd7-4197-aab2-2f40872fde72"
                name="name"
                disabled
                value={conf?.tagName}
                maxLength={500}
              />
            </div>
          </div>
        )}
        <div className="tags-options-container">
          <input
            type="checkbox"
            className="mb5 classic package-tags-checkbox"
            id="55333022-ee48-4a00-83ad-e53bb7492105"
            name="applyPackageTags"
            checked={conf?.actions?.associate}
            onChange={(evt) => {
              const confDataLocal = clone(conf)
              if (confDataLocal) {
                confDataLocal.actions.associate = evt.target.checked
                setConf(confDataLocal)
                validate(confDataLocal)
              }
            }}
          />
          <label className="package-tags-label">{translate('SETTINGS_PACKAGE_TAGS_ASSOCIATE')}</label>
        </div>
        {conf?.actions?.associate && (
          <div className="ml25">
            <div className="form-group md-form-group w100p mb5">
              <Select
                value={pluck(conf?.tags, 'value')}
                name="role"
                placeholder={translate('SELECT_TAGS_PLACEHOLDER')}
                onChange={(options: any) => {
                  const confData = clone(conf)
                  if (confData) {
                    confData.tags = options
                    setConf((data) => (data ? { ...data, tags: confData.tags } : data))
                    validate(confData)
                  }
                }}
                options={Utils.transformToSelectOptions(options?.tagsForPicker, 'id', 'name', null, true)}
                searchable
                isClearable={false}
                multi
                className={isSubmitted && !isValid ? 'tags-error' : ''}
              />
            </div>
            {isSubmitted && !isValid && (
              <div className="alert danger fs12">{translate('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PackageTags
